import { useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@london-life/ui/components/dialog';
import { Switch } from '@london-life/ui/components/switch';
import {
  RAIL_ORDERS,
  TRAIN_KINDS,
  formatPence,
  railStopsOf,
  servicesTo,
  sortServices,
  type RailContent,
  type RailKind,
  type RailOrder,
  type RailService,
  type PlayerTrainOffer,
  type RailCoContent,
  type TrainRequest,
} from '@london-life/contracts';

type Tab = 'all' | RailKind | 'sleeper';
const TABS: { id: Tab; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'metro', label: 'Tube & DLR' },
  { id: 'rail', label: 'National Rail' },
  { id: 'sleeper', label: 'Sleeper' },
];
const ORDER_LABELS: Record<RailOrder, string> = { cheapest: 'Cheapest', rated: 'Top rated', comfiest: 'Comfiest' };
const KIND_LABELS: Record<RailKind, string> = { metro: 'Tube & DLR', rail: 'National Rail' };
const stars = (comfort: number) => '★'.repeat(comfort) + '☆'.repeat(3 - comfort);
const serviceName = (service: RailService) => `${service.line.name} · ${service.first ? 'First Class' : 'Standard'}`;
const SAFETY = ['⚠️ Risky', '✔️ Safe', '🛡️ Very safe'];
// a row in the list: one of the station's lines, or a player's train company (Rail Co.)
type Choice =
  | { key: string; tab: Tab; farePence: number; rating: number; comfort: number; service: RailService }
  | { key: string; tab: Tab; farePence: number; rating: number; comfort: number; offer: PlayerTrainOffer };
const ORDERS: Record<RailOrder, (a: Choice, b: Choice) => number> = {
  cheapest: (a, b) => a.farePence - b.farePence,
  rated: (a, b) => b.rating - a.rating || a.farePence - b.farePence,
  comfiest: (a, b) => b.comfort - a.comfort || a.farePence - b.farePence,
};

// The ticket office at King's Cross, as Lagos Life's Jibowu Motor Park tickets: where to (the map's own stations
// and airports, the cities up the line opening soon), the trains that run there (Tube & DLR, National Rail, and
// sleepers to come) cheapest, top rated or comfiest first, travel insurance, and the total. A player away from
// the station is taken there by cab, included.
export function TrainsSheet({
  open,
  onClose,
  rail,
  offers,
  railco,
  isOpen,
  areaOf,
  atStation,
  walletPence,
  sending,
  onBook,
  onTerminal,
}: {
  open: boolean;
  onClose: () => void;
  rail: RailContent;
  // players' trains (Rail Co.) running from the station tonight, and their types
  offers: PlayerTrainOffer[];
  railco: RailCoContent;
  // is a place open on the map (a stop whose place is hidden takes no trains)
  isOpen: (placeId: string) => boolean;
  // the area a stop's place is in, under its name
  areaOf: (placeId: string) => string;
  atStation: boolean;
  walletPence: number | null;
  sending: boolean;
  onBook: (ticket: TrainRequest) => void;
  // the station itself (the place's own card)
  onTerminal?: () => void;
}) {
  const stops = useMemo(() => railStopsOf(rail).filter((stop) => isOpen(stop.placeId)), [rail, isOpen]);
  const [to, setTo] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('all');
  const [order, setOrder] = useState<RailOrder>('cheapest');
  const [chosen, setChosen] = useState<string | null>(null);
  const [covered, setCovered] = useState(true);
  useEffect(() => {
    if (!open) return;
    setTo(stops[0]?.placeId ?? null);
    setTab('all');
    setOrder('cheapest');
    setCovered(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const stop = stops.find((item) => item.placeId === to) ?? null;
  const choicesTo = useCallback(
    (placeId: string): Choice[] => {
      const item = stops.find((entry) => entry.placeId === placeId);
      if (!item) return [];
      const lines: Choice[] = sortServices(servicesTo(rail, item), 'cheapest').map((service) => ({
        key: `${service.line.id}:${service.first ? 'first' : 'standard'}`,
        tab: service.line.kind,
        farePence: service.farePence,
        rating: service.line.rating,
        comfort: service.comfort,
        service,
      }));
      const players: Choice[] = offers
        .filter((offer) => offer.route === placeId && railco.trains[offer.kind])
        .map((offer) => ({
          key: `${offer.companyId}:${offer.kind}`,
          tab: offer.kind === 'sleeper' ? 'sleeper' : 'rail',
          farePence: offer.farePence,
          rating: offer.stars ?? 0,
          comfort: TRAIN_KINDS.indexOf(offer.kind) + 1,
          offer,
        }));
      return [...lines, ...players];
    },
    [stops, rail, offers, railco],
  );
  const choices = useMemo(
    () =>
      stop
        ? choicesTo(stop.placeId)
            .filter((choice) => tab === 'all' || choice.tab === tab)
            .sort(ORDERS[order])
        : [],
    [choicesTo, stop, tab, order],
  );
  // the first in the list until one is picked (and again when the list changes under it)
  const picked = choices.find((choice) => choice.key === chosen) ?? choices[0] ?? null;
  const cover = covered ? rail.insurance.pence : 0;
  const total = picked ? picked.farePence + cover : 0;
  const short = walletPence === null || !picked ? 0 : Math.max(0, total - walletPence);
  const cheapest = (placeId: string) => {
    const fares = choicesTo(placeId).map((choice) => choice.farePence);
    return fares.length ? Math.min(...fares) : 0;
  };
  const choiceName = (choice: Choice) =>
    'service' in choice
      ? serviceName(choice.service)
      : `${choice.offer.name} · ${railco.trains[choice.offer.kind].label}`;
  const book = (choice: Choice) =>
    'service' in choice
      ? { to: stop!.placeId, line: choice.service.line.id, first: choice.service.first, covered }
      : { to: stop!.placeId, line: choice.offer.kind, first: false, covered, company: choice.offer.companyId };
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="city-card city-phone flights-sheet trains-sheet">
        <DialogTitle>🎫 {rail.name} · Tickets</DialogTitle>
        <DialogDescription>Pick a train, pay and board</DialogDescription>
        <strong className="flights-step trains-step">WHERE TO?</strong>
        <div className="trains-to" role="radiogroup" aria-label="Where to?">
          {stops.map((item) => (
            <button
              type="button"
              key={item.placeId}
              role="radio"
              aria-checked={to === item.placeId}
              className={to === item.placeId ? 'active' : ''}
              // tinted with the colour of the first line that runs there
              style={{ ['--line' as string]: servicesTo(rail, item)[0]?.line.colours.band }}
              onClick={() => {
                setTo(item.placeId);
                setChosen(null);
              }}
            >
              <strong>{item.name}</strong>
              <small>{areaOf(item.placeId)}</small>
              <em>from {formatPence(cheapest(item.placeId))}</em>
            </button>
          ))}
          {rail.cities
            .filter((city) => city.visible)
            .map((city) => (
              <button type="button" key={city.id} role="radio" aria-checked={false} disabled className="soon">
                <strong>
                  {city.icon} {city.name}
                </strong>
                <small>
                  {city.line} · {city.station}
                </small>
                <em>opens soon</em>
              </button>
            ))}
        </div>
        {!stops.length && <p className="neighbours-note">No trains are running right now. Check back soon 🚆</p>}
        {stop && (
          <>
            <div className="flights-tabs trains-tabs" role="tablist" aria-label="Kind of train">
              {TABS.map((item) => (
                <button
                  type="button"
                  key={item.id}
                  role="tab"
                  aria-selected={tab === item.id}
                  className={tab === item.id ? 'active' : ''}
                  onClick={() => setTab(item.id)}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <div className="trains-order" role="radiogroup" aria-label="Order">
              {RAIL_ORDERS.map((item) => (
                <button
                  type="button"
                  key={item}
                  role="radio"
                  aria-checked={order === item}
                  className={order === item ? 'active' : ''}
                  onClick={() => setOrder(item)}
                >
                  {ORDER_LABELS[item]}
                </button>
              ))}
            </div>
            {!choices.length ? (
              <p className="neighbours-note">
                {tab === 'sleeper'
                  ? "No sleepers run there tonight. Sleepers are players' own trains: start one in Rail Co. 🚆"
                  : `No ${TABS.find((item) => item.id === tab)?.label} trains go there.`}
              </p>
            ) : (
              <div className="trains-list" role="radiogroup" aria-label="Trains">
                {choices.map((choice) => {
                  const on = picked?.key === choice.key;
                  return (
                    <button
                      type="button"
                      key={choice.key}
                      role="radio"
                      aria-checked={on}
                      aria-label={choiceName(choice)}
                      className={on ? 'active' : ''}
                      onClick={() => setChosen(choice.key)}
                    >
                      {'service' in choice ? (
                        <>
                          <span
                            className="trains-rating"
                            aria-hidden="true"
                            style={{ ['--rating' as string]: `${(choice.service.line.rating / 5) * 100}%` }}
                          >
                            <i style={{ background: choice.service.line.colours.band }} />
                          </span>
                          <span className="flights-to-name">
                            <strong>{serviceName(choice.service)}</strong>
                            <small>
                              {KIND_LABELS[choice.service.line.kind]} · {stars(choice.service.comfort)} ·{' '}
                              {choice.service.line.rating.toFixed(1)}
                            </small>
                            <small>{choice.service.perks}</small>
                          </span>
                        </>
                      ) : (
                        <>
                          <span
                            className="trains-paint"
                            aria-hidden="true"
                            style={{
                              ['--body' as string]: choice.offer.body,
                              ['--stripe' as string]: choice.offer.stripe,
                            }}
                          >
                            {railco.trains[choice.offer.kind].icon}
                          </span>
                          <span className="flights-to-name">
                            <strong>{choiceName(choice)}</strong>
                            <small>
                              {SAFETY[choice.offer.safety] ?? SAFETY[1]} ·{' '}
                              {choice.offer.stars
                                ? `★ ${choice.offer.stars.toFixed(1)} (${choice.offer.ratings})`
                                : 'New'}{' '}
                              · {choice.offer.seatsLeft} seats left
                            </small>
                            <small>{choice.offer.slogan ?? railco.trains[choice.offer.kind].perk}</small>
                          </span>
                        </>
                      )}
                      <em>{formatPence(choice.farePence)}</em>
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}
        {stop && picked && (
          <div className="trains-total">
            <small>
              {rail.name} → {stop.name} · {choiceName(picked)}
            </small>
            <label className="trains-cover">
              <span>
                <strong>🛡️ Travel insurance +{formatPence(rail.insurance.pence)}</strong>
                <small>
                  Bags, pickpockets on the platform. 1 in {Math.round(100 / Math.max(1, rail.insurance.lossPercent))}{' '}
                  bags go missing without it
                </small>
              </span>
              <Switch checked={covered} onCheckedChange={setCovered} aria-label="Travel insurance" />
            </label>
            {!atStation && <small>🚕 A cab to {rail.name} is included</small>}
            <div className="trains-pay">
              <span>
                <small>Fare{covered ? ', cover' : ''}</small>
                <strong>{formatPence(total)}</strong>
              </span>
              <button
                type="button"
                className="city-card-go"
                disabled={sending || short > 0}
                onClick={() => onBook(book(picked))}
              >
                {short > 0 ? `${formatPence(short)} short` : sending ? 'Buying…' : 'Pay & board 🚆'}
              </button>
            </div>
          </div>
        )}
        {onTerminal && (
          <button type="button" className="flights-terminal" onClick={onTerminal}>
            🚉 Go into the station
          </button>
        )}
      </DialogContent>
    </Dialog>
  );
}
